import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FiCheck, FiHash, FiSearch, FiX } from 'react-icons/fi';
import { colors, spacing, borderRadius, typography } from '../theme/colors';

/**
 * SerialSelectPopover - A small button that shows available serials in a dropdown.
 * User clicks serials to select/deselect them.
 *
 * Props:
 * - availableSerials: string[] - list of available serial numbers
 * - selectedSerials: string[] - currently selected serial numbers
 * - requiredCount: number - how many serials are needed (quantity)
 * - onChange: (serials: string[]) => void - called when selection changes
 * - disabled?: boolean
 */
const SerialSelectPopover = ({ availableSerials = [], selectedSerials = [], requiredCount = 0, onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  // Reset + focus search when opening
  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  const filteredSerials = useMemo(() => {
    if (!search.trim()) return availableSerials;
    const q = search.toLowerCase();
    return availableSerials.filter(s => String(s).toLowerCase().includes(q));
  }, [availableSerials, search]);

  // Position dropdown relative to button, rendered via portal
  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left });
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    updatePosition();
    const handleClick = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const handleScroll = () => updatePosition();
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open, updatePosition]);

  const toggleSerial = (serial) => {
    if (disabled) return;
    const isSelected = selectedSerials.includes(serial);
    let next;
    if (isSelected) {
      next = selectedSerials.filter(s => s !== serial);
    } else {
      if (selectedSerials.length >= requiredCount) {
        // Replace oldest selection (or just don't add)
        return;
      }
      next = [...selectedSerials, serial];
    }
    onChange(next);
  };

  const selectAll = () => {
    if (disabled) return;
    // Fill up to requiredCount from the currently filtered list, keeping existing selections
    const room = Math.max(0, requiredCount - selectedSerials.length);
    if (room === 0) return;
    const additions = filteredSerials
      .filter(s => !selectedSerials.includes(s))
      .slice(0, room);
    onChange([...selectedSerials, ...additions]);
  };

  const clearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  const selectedCount = selectedSerials.length;

  return (
    <Wrapper>
      <TriggerButton
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        disabled={disabled}
        $hasSelection={selectedCount > 0}
        $complete={selectedCount === requiredCount && requiredCount > 0}
      >
        <FiHash size={12} />
        {selectedCount > 0 ? `${selectedCount}/${requiredCount} selected` : `Select ${requiredCount} serial(s)`}
      </TriggerButton>

      {open && createPortal(
        <Dropdown ref={dropdownRef} style={{ top: pos.top, left: pos.left }} onClick={e => e.stopPropagation()}>
          <DropdownHeader>
            <HeaderTitle>
              Available Serials (
              {search.trim() ? `${filteredSerials.length}/${availableSerials.length}` : availableSerials.length}
              )
            </HeaderTitle>
            <HeaderActions>
              {requiredCount > selectedCount && filteredSerials.length > 0 && (
                <SmallBtn onClick={selectAll}>
                  Select {Math.min(requiredCount - selectedCount, filteredSerials.filter(s => !selectedSerials.includes(s)).length)}
                </SmallBtn>
              )}
              {selectedCount > 0 && <SmallBtn onClick={clearAll}>Clear</SmallBtn>}
            </HeaderActions>
          </DropdownHeader>
          <SearchBar>
            <FiSearch size={12} />
            <SearchInput
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search serial..."
              onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } }}
            />
            {search && (
              <ClearSearchBtn type="button" onClick={() => { setSearch(''); searchRef.current?.focus(); }}>
                <FiX size={12} />
              </ClearSearchBtn>
            )}
          </SearchBar>
          <SerialList>
            {availableSerials.length === 0 ? (
              <EmptyMsg>No serials available</EmptyMsg>
            ) : filteredSerials.length === 0 ? (
              <EmptyMsg>No serials match "{search}"</EmptyMsg>
            ) : (
              filteredSerials.map(serial => {
                const isSelected = selectedSerials.includes(serial);
                const isDisabledItem = !isSelected && selectedCount >= requiredCount;
                return (
                  <SerialItem
                    key={serial}
                    $selected={isSelected}
                    $disabled={isDisabledItem}
                    onClick={() => !isDisabledItem && toggleSerial(serial)}
                  >
                    <Checkbox $checked={isSelected}>
                      {isSelected && <FiCheck size={10} />}
                    </Checkbox>
                    <SerialLabel>{serial}</SerialLabel>
                  </SerialItem>
                );
              })
            )}
          </SerialList>
          {selectedCount > 0 && (
            <DropdownFooter>
              {selectedCount}/{requiredCount} selected
            </DropdownFooter>
          )}
        </Dropdown>,
        document.body
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid ${props => props.$complete ? colors.success.main : props.$hasSelection ? colors.info.main : colors.border.main};
  border-radius: ${borderRadius.sm};
  background: ${props => props.$complete ? colors.success.main + '10' : props.$hasSelection ? colors.info.main + '10' : colors.background.default};
  color: ${props => props.$complete ? colors.success.main : props.$hasSelection ? colors.info.main : colors.text.secondary};
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  min-width: 130px;

  &:hover:not(:disabled) {
    border-color: ${colors.primary.main};
    color: ${colors.primary.main};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Dropdown = styled.div`
  position: fixed;
  z-index: 10000;
  min-width: 220px;
  max-width: 300px;
  background: ${colors.background.default};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid ${colors.border.light};
  background: ${colors.background.paper};
  border-radius: ${borderRadius.md} ${borderRadius.md} 0 0;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-bottom: 1px solid ${colors.border.light};
  background: ${colors.background.default};
  color: ${colors.text.secondary};
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: none;
  font-size: 12px;
  color: ${colors.text.primary};
  padding: 2px 0;
  min-width: 0;
  &::placeholder { color: ${colors.text.tertiary}; }
`;

const ClearSearchBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  background: ${colors.neutral[200]};
  color: ${colors.text.secondary};
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  &:hover { background: ${colors.error.main}; color: white; }
`;

const HeaderTitle = styled.span`
  font-size: 11px;
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 6px;
`;

const SmallBtn = styled.button`
  padding: 2px 8px;
  font-size: 10px;
  border: 1px solid ${colors.border.main};
  border-radius: 3px;
  background: ${colors.background.default};
  color: ${colors.primary.main};
  cursor: pointer;

  &:hover {
    background: ${colors.primary.main};
    color: white;
    border-color: ${colors.primary.main};
  }
`;

const SerialList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  padding: 4px 0;
`;

const EmptyMsg = styled.div`
  padding: 12px;
  text-align: center;
  font-size: 12px;
  color: ${colors.text.tertiary};
`;

const SerialItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.4 : 1};
  background: ${props => props.$selected ? colors.primary.main + '08' : 'transparent'};

  &:hover {
    background: ${props => props.$disabled ? 'transparent' : props.$selected ? colors.primary.main + '12' : colors.background.hover};
  }
`;

const Checkbox = styled.div`
  width: 16px;
  height: 16px;
  border: 1.5px solid ${props => props.$checked ? colors.primary.main : colors.border.main};
  border-radius: 3px;
  background: ${props => props.$checked ? colors.primary.main : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const SerialLabel = styled.span`
  font-size: 12px;
  font-family: 'SF Mono', 'Consolas', monospace;
  color: ${colors.text.primary};
`;

const DropdownFooter = styled.div`
  padding: 6px 10px;
  border-top: 1px solid ${colors.border.light};
  font-size: 11px;
  color: ${colors.text.secondary};
  text-align: center;
  background: ${colors.background.paper};
  border-radius: 0 0 ${borderRadius.md} ${borderRadius.md};
`;

export default SerialSelectPopover;
